import { Button, InputAdornment, Stack, TextField } from '@mui/material'
import { RegModalWrapper } from '../ModalContent'
import pxToRem from '../../../../helpers/pxToRem'
import styles from '../index.module.scss'
import { UserRegSx } from '../../../../components/TextField'
import dayjs, { Dayjs } from 'dayjs'
import { Fragment, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { LightTooltip } from '../../../../components/Tooltip'
import {
    AccreditationDocument,
    AccreditationDocument_v2,
    AccreditationRequiredField,
    TeacherEmploymentSetting,
} from '../../../../redux/types'
import {
    ECCQualificationLevels,
    ECCQualToTitle,
    ESQualificationOptions,
} from '../../../../firebase/types-teacher'
import FileUpload, { FileType } from '../../../../components/FileUpload'
import { documentUploadHandler, requiredDateHandler } from './helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Control, Path, useForm, UseFormSetValue } from 'react-hook-form'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import {
    accreditationDocFormSchema,
    AccreditationDocumentConfig,
} from '../../../../firebase/types-accreditation'
import { selectUserUid } from '../../../../redux/userSlice'
import { makeSelectAccreditationByType } from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { logger } from '../../../../helpers/logger'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'
import { db } from '../../../../firebase/configs'
import { push, ref, update } from 'firebase/database'
dayjs.extend(customParseFormat)

// const ECC_QUALIFICATIONS = new Set([
//     'certThree',
//     'dimploma',
//     'earlyChildhoodTrained',
//     'other',
//     'noFormalTraining',
// ])
// const ES_QUALIFICATIONS = new Set([
//     'certThree',
//     'dimploma',
//     'other',
//     'noFormalTraining',
// ])

export type TeachingDocumentationFormValues = z.infer<
    typeof accreditationDocFormSchema
>

const getImageUrl = (accred?: any, expired?: boolean): FileType | undefined => {
    if (expired) return undefined
    let imageUrl = accred?.imageURL
    if (imageUrl) {
        return { file: imageUrl, fileName: accred?.docTitle }
    } else {
        return undefined
    }
}

export const TeachingDocumentationWrapper = ({
    details,
    employmentSetting,
}: {
    details: AccreditationDocumentConfig
    employmentSetting?: TeacherEmploymentSetting
    hideCloseBtn?: boolean
}) => {
    logger.debug(`Rendering ${details.type}`)

    const selector = useMemo(makeSelectAccreditationByType, [])
    const rawDoc = useSelector((state: RootState) =>
        selector(state, details.type)
    )

    const strictSchema = useMemo(() => {
        return accreditationDocFormSchema.check((ctx) => {
            if (ctx.value.imageURL === undefined) {
                ctx.issues.push({
                    code: 'custom',
                    message: 'Incomplete*',
                    input: ctx.value.imageURL,
                    path: ['imageURL'],
                })
            }

            switch (details.required_datefield) {
                case 'registrationDate': {
                    if (
                        !ctx.value.registrationDate ||
                        !ctx.value.registrationDate.isValid()
                    )
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value.registrationDate,
                            message: 'Incomplete*',
                        })
                    break
                }
                case 'expiryDate': {
                    if (
                        !ctx.value.expiryDate ||
                        !ctx.value.expiryDate.isValid()
                    )
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value.expiryDate,
                            message: 'Incomplete*',
                        })
                    break
                }
            }

            switch (details.type) {
                case 'earlyChildcareQualification': {
                    if (
                        !ctx.value.qualification
                        // ||
                        // !ECC_QUALIFICATIONS.has(ctx.value.qualification)
                    )
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value.qualification,
                            message: 'Incomplete*',
                        })
                    break
                }
                case 'supportStaffQualification': {
                    if (
                        !ctx.value.qualification
                        // ||
                        // !ES_QUALIFICATIONS.has(ctx.value.qualification)
                    )
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value.qualification,
                            message: 'Incomplete*',
                        })
                    break
                }
            }

            if (
                details.reference_required &&
                (!ctx.value.reference ||
                    ctx.value.reference.trim().length === 0)
            ) {
                ctx.issues.push({
                    code: 'custom',
                    message: 'Incomplete*',
                    input: ctx.value.reference,
                    path: ['reference'],
                })
            }

            if (details.fields) {
                details.fields.forEach((el) => {
                    const value = ctx.value[el.key]

                    if (el.required && value === undefined) {
                        ctx.issues.push({
                            code: 'custom',
                            message: 'Incomplete*',
                            input: ctx.value[el.key],
                            path: [`${el.key}`],
                        })
                        return
                    }

                    switch (el.input) {
                        case 'text': {
                            if (typeof value !== 'string') {
                                ctx.issues.push({
                                    code: 'invalid_type',
                                    input: value,
                                    expected: 'string',
                                })
                                return
                            }
                            if (
                                el.required &&
                                (value as string).trim().length < 1
                            ) {
                                ctx.issues.push({
                                    code: 'custom',
                                    message: 'Incomplete*',
                                    input: ctx.value[el.key],
                                    path: [`${el.key}`],
                                })
                            }
                            if (el.pattern) {
                                const regex = new RegExp(el.pattern)
                                if (!regex.test(value)) {
                                    ctx.issues.push({
                                        code: 'custom',
                                        message: 'Incomplete*',
                                        input: ctx.value[el.key],
                                        path: [`${el.key}`],
                                    })
                                }
                            }
                            break
                        }
                        case 'date': {
                            if (
                                el.required &&
                                (value === undefined ||
                                    !(value as Dayjs).isValid())
                            ) {
                                ctx.issues.push({
                                    code: 'custom',
                                    message: 'Incomplete*',
                                    input: ctx.value[el.key],
                                    path: [`${el.key}`],
                                })
                            }
                            break
                        }
                        case 'select': {
                            if (el.multiple) {
                                if (
                                    el.required &&
                                    (value === undefined ||
                                        ((value || []) as any[]).length === 0)
                                ) {
                                    ctx.issues.push({
                                        code: 'custom',
                                        message: 'At least 1 options required',
                                        input: value,
                                        path: [`${el.key}`],
                                    })
                                }
                            } else {
                                if (el.required && value === undefined) {
                                    ctx.issues.push({
                                        code: 'custom',
                                        message: 'Incomplete*',
                                        input: value,
                                        path: [`${el.key}`],
                                    })
                                }
                            }
                            break
                        }
                    }
                })
            }
        })
    }, [details])

    if (rawDoc.status === 'loading') return <LoadingSpinner />
    if (rawDoc.status === 'error') throw rawDoc.error
    if (rawDoc.status === 'success') {
        return (
            <TeachingDocumentation_v2
                details={details}
                id={rawDoc.id}
                schema={strictSchema}
                document={rawDoc.data}
                employmentSetting={employmentSetting}
            />
        )
    }
}

interface ITeachingDocumentation {
    details: AccreditationDocumentConfig
    id: string | undefined
    document: AccreditationDocument_v2
    schema: typeof accreditationDocFormSchema
    hideCloseBtn?: boolean
    employmentSetting?: TeacherEmploymentSetting
}
const TeachingDocumentation_v2 = ({
    details,
    employmentSetting,
    schema,
    document,
    id,
}: ITeachingDocumentation) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        control,
        handleSubmit,
        formState: { isValid: isComplete, isDirty },
        setValue,
        watch,
    } = useForm<TeachingDocumentationFormValues>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            imageURL: document.imageURL || undefined,
            reference: document.reference || undefined,
            registrationDate: document.registrationDate
                ? dayjs(document.registrationDate, 'DD-MM-YYYY')
                : undefined,
            expiryDate: document.expiryDate
                ? dayjs(document.expiryDate, 'DD-MM-YYYY')
                : undefined,

            qualification: document.qualification || undefined,
            classification: document.classification || undefined,

            courseType: document.courseType || undefined,
            renewal_frequency_years:
                details.renewal_frequency_years || undefined,
            website_link: details.website_link || undefined,
        },
    })
    const website_link = watch('website_link')

    const [file, setFile] = useState<FileType | undefined>(
        getImageUrl(document)
    )
    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })
        setFile({ file: file, fileName: file.name })
        setValue('imageURL', f, { shouldDirty: true, shouldValidate: true })
    }

    const handleUpdate = async (data: TeachingDocumentationFormValues) => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }

            if (!isDirty) {
                logger.debug('No changes, skip update')
                return
            }

            const selectedDateField = details.required_datefield
            const selectedDate =
                selectedDateField !== null ? data[selectedDateField] : undefined
            const datesData = requiredDateHandler(
                selectedDate && dayjs.isDayjs(selectedDate)
                    ? selectedDate
                    : null,
                selectedDateField,
                data.renewal_frequency_years
            )
            const { fileUrl, fileType, docTitle } = await documentUploadHandler(
                userId,
                file,
                {
                    url: document.imageURL,
                    type: document.type,
                    docTitle: document.docTitle,
                }
            )

            const toUpdate = {
                imageURL: fileUrl,
                type: fileType,
                docTitle: docTitle,

                ...datesData,
                accreditationType: details.type,
                reference: data.reference || null,

                dateSubmitted: dayjs().tz().format('DD-MM-YYYY'),
                qualification: data.qualification || null,
                courseType: data.courseType || null,
                classification: data.classification || null,
                state: 'submitted',
            }

            if (typeof id === 'string') {
                await update(
                    ref(db, `teachers/${userId}/accreditation/${id}`),
                    toUpdate
                )
            } else {
                await push(
                    ref(db, `teachers/${userId}/accreditation`),
                    toUpdate
                )
            }
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    const titleForDoc_v2 = (detail: AccreditationDocumentConfig) =>
        `Teaching Documentation - ${detail.short_title || ''}${
            detail.optional ? ' (Optional)' : ''
        }`

    const removeFile = () => {
        setFile(undefined)
        setValue('imageURL', undefined, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        })
    }

    return (
        <>
            <RegModalWrapper
                title={titleForDoc_v2(details)}
                handleUpdate={handleSubmit(handleUpdate)}
                state={document.state}
                isAllComplete={isComplete}
            >
                <Stack gap={pxToRem(24)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>{details.long_title}</h5>
                        {document.state === 'incomplete' ? (
                            <Stack className={styles.infoBox}>
                                <small>{details.details_description}</small>
                            </Stack>
                        ) : (
                            <Stack
                                direction="row"
                                alignItems={'center'}
                                gap={pxToRem(12)}
                            >
                                <LightTooltip
                                    placement="bottom-start"
                                    title={
                                        <p className={styles.p}>
                                            {details.details_description}
                                        </p>
                                    }
                                >
                                    {/* prettier-ignore */}
                                    <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill={styles.shades100} />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg>
                                </LightTooltip>
                                <p className={styles.p}>
                                    Why we need this information
                                </p>
                            </Stack>
                        )}
                    </Stack>
                    {details.fields ? (
                        <TeachingDocumentationFields
                            control={control}
                            fields={details.fields}
                            setValue={setValue}
                        />
                    ) : (
                        <Stack gap={pxToRem(24)}>
                            <Stack>
                                {details.type ===
                                'earlyChildcareQualification' ? (
                                    <FormSelect
                                        name="qualification"
                                        control={control}
                                        controlProps={{
                                            sx: UserRegSx,
                                        }}
                                        options={ECCQualificationLevels.sort(
                                            (a, b) => a.localeCompare(b)
                                        ).map((el, i) => ({
                                            label: ECCQualToTitle(el),
                                            value: el,
                                        }))}
                                        props={{ label: 'Qualification' }}
                                    />
                                ) : null}
                                {details.type ===
                                'supportStaffQualification' ? (
                                    <FormSelect
                                        name="qualification"
                                        control={control}
                                        controlProps={{
                                            sx: UserRegSx,
                                        }}
                                        options={ESQualificationOptions.sort(
                                            (a, b) =>
                                                a.label.localeCompare(b.label)
                                        )}
                                        props={{ label: 'Qualification' }}
                                    />
                                ) : null}
                            </Stack>
                            {details.required_datefield === null ? null : (
                                <FormDatePicker_v2
                                    control={control}
                                    name={`${details.required_datefield}`}
                                    datePickerProps={{
                                        format: 'DD-MM-YYYY',
                                        minDate: getMinDate(
                                            details?.required_datefield
                                        ),
                                        maxDate: getMaxDate(
                                            details?.required_datefield
                                        ),
                                        sx: UserRegSx,
                                        label: getDateLabel(
                                            details?.required_datefield
                                        ),
                                    }}
                                />
                            )}
                            {details.reference_required ? (
                                <FormInputText
                                    control={control}
                                    name={'reference'}
                                    label="Reference"
                                    sx={UserRegSx}
                                />
                            ) : null}
                        </Stack>
                    )}

                    {website_link !== undefined &&
                    website_link.trim().length > 0 ? (
                        <TextField
                            value="Resources"
                            sx={UserRegSx}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button
                                                variant="text"
                                                color="primaryBlue500"
                                                onClick={() =>
                                                    window.open(
                                                        website_link,
                                                        '_blank'
                                                    )
                                                }
                                            >
                                                View Website
                                            </Button>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    ) : null}

                    <RegFileUploadLabel file={file} reset={removeFile} />
                    {!file && details.upload_instructions ? (
                        <Stack
                            direction="row"
                            alignItems={'center'}
                            gap={pxToRem(12)}
                        >
                            <LightTooltip
                                placement="bottom-start"
                                title={
                                    <p className={styles.p}>
                                        {details.upload_instructions}
                                    </p>
                                }
                            >
                                {/* prettier-ignore */}
                                <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill={styles.shades100} />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg>
                                {/* <svg className={styles.vitInfoIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <circle cx="10" cy="10" r="10" fill="#C465A8" />{' '} <path d="M8.996 15V8.652H10.7V15H8.996ZM9.836 7.344C9.508 7.344 9.252 7.264 9.068 7.104C8.884 6.944 8.792 6.716 8.792 6.42C8.792 6.148 8.884 5.928 9.068 5.76C9.26 5.584 9.516 5.496 9.836 5.496C10.164 5.496 10.42 5.58 10.604 5.748C10.788 5.908 10.88 6.132 10.88 6.42C10.88 6.7 10.784 6.924 10.592 7.092C10.408 7.26 10.156 7.344 9.836 7.344Z" fill="white" />{' '} </svg> */}
                            </LightTooltip>
                            <p
                                className={styles.p}
                                style={{ fontSize: pxToRem(14) }}
                            >
                                What do I upload?
                            </p>
                        </Stack>
                    ) : null}
                    <FileUpload
                        file={file}
                        onChangeCallback={onFileChange}
                        extensions={'document'}
                        iconColor={styles.primaryYellow500}
                        fileSizeLimitMb={
                            employmentSetting === 'earlyChildcare' ? null : 10
                        }
                        compressionRatio={
                            employmentSetting === 'earlyChildcare' ? null : 0.6
                        }
                    />
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export const TeachingDocumentationFields = ({
    control,
    fields,
    setValue,
}: {
    control: Control<TeachingDocumentationFormValues>
    setValue: UseFormSetValue<TeachingDocumentationFormValues>
    fields: NonNullable<AccreditationDocumentConfig['fields']>
}) => {
    return fields.map((el, i) => {
        const name = el.key as Path<TeachingDocumentationFormValues>
        switch (el.input) {
            case 'text': {
                return (
                    <Fragment key={`accredInput_${el.key}_${i}`}>
                        <FormInputText
                            control={control}
                            name={name}
                            label={el.label}
                            sx={UserRegSx}
                        />
                    </Fragment>
                )
            }
            case 'date': {
                return (
                    <Fragment key={`accredInput_${el.key}_${i}`}>
                        <FormDatePicker_v2
                            control={control}
                            name={name}
                            datePickerProps={{
                                sx: UserRegSx,
                                format: 'DD-MM-YYYY',
                                label: el.label,
                            }}
                        />
                    </Fragment>
                )
            }
            case 'select': {
                return (
                    <Fragment key={`accredInput_${el.key}_${i}`}>
                        <FormSelect
                            control={control}
                            name={name}
                            props={{
                                label: el.label,
                            }}
                            controlProps={{
                                sx: UserRegSx,
                            }}
                            options={el.options.map((option) => ({
                                label: option.label,
                                value: option.value,
                            }))}
                            onChange={(e) => {
                                const option = el.options.find(
                                    (el) => el.value === e.target.value
                                )

                                if (option === undefined) return
                                const { label, value, ...others } = option
                                Object.entries(others).forEach(([k, v]) => {
                                    logger.debug(`${k} : ${v}`)
                                    try {
                                        if (
                                            k in
                                            accreditationDocFormSchema.shape
                                        ) {
                                            logger.debug(`setting value`)
                                            setValue(
                                                k as Path<TeachingDocumentationFormValues>,
                                                v as any
                                            )
                                        }
                                    } catch (e) {
                                        logger.debug(
                                            `Failed to apply option value`
                                        )
                                    }
                                })
                            }}
                        />
                    </Fragment>
                )
            }
        }
    })
}

export const getRequiredDate = (
    accred?: AccreditationDocument,
    variant?: AccreditationRequiredField
) => {
    if (!accred) {
        return null
    }
    switch (variant) {
        case null:
            return null
        case 'registrationDate':
            return accred.registrationDate
                ? dayjs(accred.registrationDate, 'DD-MM-YYYY')
                : null
        default:
            return accred.expiryDate
                ? dayjs(accred.expiryDate, 'DD-MM-YYYY')
                : null
    }
}

export const getDateLabel = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return 'Date Completed'
        // case 'expiryDate': case undefined: return 'Expiry Date';
        default:
            return 'Expiry Date'
    }
}

export const getMinDate = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return undefined
        case 'expiryDate':
            return dayjs()
        default:
            return dayjs()
    }
}

export const getMaxDate = (variant?: AccreditationRequiredField) => {
    switch (variant) {
        case 'registrationDate':
            return dayjs()
        case 'expiryDate':
            return undefined
        default:
            return undefined
    }
}

export default TeachingDocumentation_v2
